import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, DollarSign, Percent, CheckCircle, AlertCircle, Tag } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const BillingSettings = () => {
  const [loading,    setLoading]    = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [settings,   setSettings]   = useState({
    landing_fee_rate:      0,
    fuel_surcharge_percent: 0,
    tax_rate:              0,
    airport_tax_rate:      0,
    airport_tax_label:     'Airport Fee',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/company/settings`);
      if (response.data) {
        setSettings({
          landing_fee_rate:      response.data.landing_fee_rate      || 0,
          fuel_surcharge_percent: response.data.fuel_surcharge_percent || 0,
          tax_rate:              response.data.tax_rate              || 0,
          airport_tax_rate:      response.data.airport_tax_rate      || 0,
          airport_tax_label:     response.data.airport_tax_label     || 'Airport Fee',
        });
      }
    } catch (error) {
      console.error('Error fetching billing settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setSaveStatus(null);
    try {
      await axios.put(`${API_BASE}/company/settings`, settings);
      setSaveStatus({ type: 'success', message: 'Billing settings saved successfully!' });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error saving billing settings:', error);
      setSaveStatus({ type: 'error', message: 'Error saving billing settings' });
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden p-6">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white">Billing & Fees Configuration</h3>
          <p className="text-gray-400 text-sm">Manage landing fees, fuel surcharges, and tax rates</p>
        </div>
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white disabled:opacity-50 transition-colors"
        >
          <Save className="w-4 h-4" />
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Status message */}
      {saveStatus && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
          saveStatus.type === 'success'
            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
            : 'bg-red-500/20 text-red-400 border border-red-500/30'
        }`}>
          {saveStatus.type === 'success'
            ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
            : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
          {saveStatus.message}
        </div>
      )}

      <div className="space-y-8 max-w-md">

        {/* ── Section : FBO Fees ── */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-4 border-b border-gray-700 pb-2">
            FBO Fees
          </p>
          <div className="space-y-5">

            {/* Landing Fee */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Landing Fee Rate ($)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="number" step="0.01"
                  value={settings.landing_fee_rate}
                  onChange={e => update('landing_fee_rate', parseFloat(e.target.value) || 0)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Fee charged per landing</p>
            </div>

            {/* Fuel Surcharge */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Fuel Surcharge (%)</label>
              <div className="relative">
                <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="number" step="0.1"
                  value={settings.fuel_surcharge_percent}
                  onChange={e => update('fuel_surcharge_percent', parseFloat(e.target.value) || 0)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Applied on fuel transactions</p>
            </div>
          </div>
        </div>

        {/* ── Section : Taxes ── */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-4 border-b border-gray-700 pb-2">
            Tax Rates
          </p>
          <div className="space-y-5">

            {/* State Tax */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">State Tax Rate (%)</label>
              <div className="relative">
                <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="number" step="0.01"
                  value={settings.tax_rate}
                  onChange={e => update('tax_rate', parseFloat(e.target.value) || 0)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                e.g. Texas: 8.25 — stored as percentage, converted to decimal automatically on invoices
              </p>
            </div>

            {/* Airport Fee Label */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Airport Fee Label</label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  value={settings.airport_tax_label}
                  onChange={e => update('airport_tax_label', e.target.value)}
                  placeholder="e.g. Airport Fee, Redevance KHOU"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-600 focus:outline-none focus:border-blue-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Label displayed on supplier invoices for the airport-specific fee
              </p>
            </div>

            {/* Airport Fee Rate */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Airport Fee Rate (%)</label>
              <div className="relative">
                <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="number" step="0.01"
                  value={settings.airport_tax_rate}
                  onChange={e => update('airport_tax_rate', parseFloat(e.target.value) || 0)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Airport-specific fee applied on supplier invoices — 0 to disable
              </p>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default BillingSettings;